import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, Box, Typography, Tabs, Tab, Paper, Grid, Tooltip, IconButton, Switch, FormControlLabel, Slider, Collapse, Divider } from "@material-ui/core";
import VozNatural from "./VozNatural";
import { makeStyles } from "@material-ui/core/styles";
import { MdSmartToy } from "react-icons/md";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageIcon from "@material-ui/icons/Image";
import LinkIcon from "@material-ui/icons/Link";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ExtensionIcon from "@material-ui/icons/Extension";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventNoteIcon from "@material-ui/icons/EventNote";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import InfoIcon from "@material-ui/icons/Info";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import ForumIcon from "@material-ui/icons/Forum";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import CodeIcon from "@material-ui/icons/Code";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import BusinessIcon from "@material-ui/icons/Business";
import AssignmentIcon from "@material-ui/icons/Assignment";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ChatIcon from "@material-ui/icons/Chat";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import { TOOL_CATALOG, DEFAULT_SENSITIVE_TOOLS, HABILIDADES_POR_PERFIL } from "../../constants/aiTools";
import { TOOL_ICONS, TOOL_INSTRUCTIONS } from "./toolConfig";

// Componente Error Boundary simples
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Erro no modal:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Dialog open={this.props.open} onClose={this.props.close}>
          <DialogTitle>Erro</DialogTitle>
          <DialogContent>
            <Typography>Ocorreu um erro ao carregar o formulário. Tente novamente.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.props.close}>Fechar</Button>
          </DialogActions>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      minWidth: "700px",
      maxWidth: "900px"
    }
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: "200px"
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  },
  chip: {
    margin: theme.spacing(0.25)
  },
  providerChip: {
    marginBottom: theme.spacing(2)
  },
  dialogTitle: {
    backgroundColor: "#3f51b5",
    color: "white",
    textAlign: "center",
    fontSize: "24px",
    fontWeight: "bold",
  },
  btnWrapper: {
    position: "relative",
  },
  tabPanel: {
    padding: theme.spacing(2),
    minHeight: "400px"
  },
  tabContent: {
    marginTop: theme.spacing(2)
  },
  promptField: {
    "& .MuiOutlinedInput-root": {
      minHeight: "200px",
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& .MuiOutlinedInput-input": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& .MuiInputBase-inputMultiline": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& .MuiInputBase-input": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    },
    "& textarea": {
      direction: "ltr !important",
      textAlign: "left !important",
      unicodeBidi: "normal !important"
    }
  },
  toolGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  toolCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "2px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    minHeight: 190,
    backgroundColor: "#fff",
    transition: "border-color 0.18s, box-shadow 0.18s, background-color 0.18s",
    cursor: "default",
    '&:hover': {
      boxShadow: "0 4px 16px rgba(99,102,241,0.10)",
      borderColor: "#c7d2fe",
    }
  },
  toolCardActive: {
    border: "2px solid #6366f1",
    backgroundColor: "#f5f3ff",
    '&:hover': {
      boxShadow: "0 4px 16px rgba(99,102,241,0.18)",
    }
  },
  toolHeader: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1.2),
  },
  toolIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1rem",
    flexShrink: 0,
    boxShadow: "0 2px 6px rgba(99,102,241,0.25)",
  },
  toolIconSensitive: {
    background: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)",
    boxShadow: "0 2px 6px rgba(220,38,38,0.25)",
  },
  toolTitle: {
    fontWeight: 700,
    fontSize: "0.82rem",
    color: "#1e1b4b",
    lineHeight: 1.3,
  },
  toolSlug: {
    fontSize: "0.68rem",
    color: "#a5b4fc",
    fontFamily: "monospace",
    marginTop: 1,
  },
  toolDescription: {
    fontSize: "0.78rem",
    color: "#4b5563",
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.5,
    flexGrow: 1,
  },
  toolInstruction: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontStyle: "italic",
    lineHeight: 1.4,
    marginBottom: theme.spacing(1),
  },
  toolFooter: {
    marginTop: "auto",
    paddingTop: theme.spacing(1),
    borderTop: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }
}));

const TabPanel = ({ children, value, index, className }) => (
  <div
    role="tabpanel"
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    style={{ display: value === index ? 'block' : 'none' }}
  >
    <Box className={className}>
      {children}
    </Box>
  </div>
);

const FlowBuilderAddDirectOpenAIModal = ({ open, onSave, data, close }) => {
  const classes = useStyles();
  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Configurar Agente de IA Direto",
    btn: "Salvar",
  });
  const [tabValue, setTabValue] = useState(0);
  
  const [config, setConfig] = useState({
    aiMode: "system",
    provider: "gemini",
    apiKey: "",
    model: "",
    prompt: "Você é um assistente de IA útil e amigável.",
    temperature: 0.7,
    maxTokens: 1000,
    maxMessages: 10,
    pauseMinutes: 10,
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    ttsModel: "tts-1",
    audioPercentage: 30,
    toolsEnabled: [],
    knowledgeBase: [],
    knowledgeBaseIds: [],
    entenderAudio: false,
    entenderImagem: false,
    entenderPdf: false,
    identificarComprovante: false,
    isolarNumero: false
  });

  const [knowledgeUploading, setKnowledgeUploading] = useState(false);
  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState([]);
  const [aiIntegrationSettings, setAiIntegrationSettings] = useState([]);
  const [etiquetasDaEmpresa, setEtiquetasDaEmpresa] = useState([]);
  const [systemProviders, setSystemProviders] = useState({ openai: false, gemini: false, openrouter: false });
  const imageInputRef = useRef(null);
  const [linkForm, setLinkForm] = useState({ title: "", url: "" });
  const [textForm, setTextForm] = useState({ title: "", content: "" });
  const [selectedVoice, setSelectedVoice] = useState("texto");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [ttsModel, setTtsModel] = useState("tts-1");
  const [audioPercentage, setAudioPercentage] = useState(30);

  const [showFormulario, setShowFormulario] = useState(false);
  const [formulario, setFormulario] = useState({
    // O perfil muda as regras de comportamento geradas. O miolo e o mesmo.
    perfil: 'atendimento',
    nomeEmpresa: '', responsavel: '', telefone: '', email: '', site: '', instagram: '', endereco: '',
    oqueFaz: '', servicos: '', diferencial: '', regioes: '',
    funcaoIA: '', expectativas: '',
    diasAtendimento: '', horario: '', feriados: '',
    precosServicos: '',
    perguntasFrequentes: '',
    atendimentoHoje: '', infoColeta: '', transferencia: '',
    agendamento: '', comoAgendamento: '', dadosAgendamento: '',
    tom: '', palavrasEvitar: '',
    nuncaFazer: '', procedimento: '',
    setoresTransferencia: '',
    baseConhecimento: ''
  });

  const backendBaseUrl = useMemo(() => {
    const url = getBackendUrl();
    if (!url) return "";
    return url.replace(/\/+$/, "");
  }, []);

  const buildPublicUrl = useCallback(
    (path = "") => {
      if (!path) return "";
      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }
      const sanitized = path.replace(/^\/+/, "");
      if (!backendBaseUrl) {
        return sanitized.startsWith("public/")
          ? `/${sanitized}`
          : `/public/${sanitized}`;
      }
      if (sanitized.startsWith("public/")) {
        return `${backendBaseUrl}/${sanitized}`;
      }
      return `${backendBaseUrl}/public/${sanitized}`;
    },
    [backendBaseUrl]
  );

  const resolveKnowledgeUrl = useCallback(
    (item) => {
      if (!item) return "";
      if (item.url) return buildPublicUrl(item.url);
      if (item.path) return buildPublicUrl(item.path);
      return "";
    },
    [buildPublicUrl]
  );

  const inferKnowledgeType = useCallback((item) => {
    if (item?.type) return item.type;
    if (item?.content) return "text";
    const reference = `${item?.title || ""} ${item?.url || ""} ${item?.path || ""}`.toLowerCase();
    if (/\.(png|jpe?g|gif|bmp|webp|svg)$/.test(reference)) return "image";
    if (/\.pdf$/.test(reference)) return "pdf";
    return "link";
  }, []);

  const handleCopyToClipboard = useCallback(async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copiado!");
    } catch (err) {
      console.error(err);
      toastError("Não foi possível copiar o link.");
    }
  }, []);

  const uploadKnowledgeFile = useCallback(async (file) => {
    if (!file) return "";
    setKnowledgeUploading(true);
    const formData = new FormData();
    formData.append("medias", file);

    try {
      const { data } = await api.post("/flowbuilder/content", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (Array.isArray(data)) {
        return data[0];
      }

      if (typeof data === "string") {
        return data;
      }

      return data?.url || data?.path || "";
    } catch (err) {
      console.error(err);
      toastError(err);
      return "";
    } finally {
      setKnowledgeUploading(false);
    }
  }, []);

  const handleKnowledgeFileAdd = useCallback(async (file, type, values, setFieldValue) => {
    if (!file) return;
    const storedPath = await uploadKnowledgeFile(file);

    if (!storedPath) {
      toast.error("Não foi possível enviar o arquivo.");
      return;
    }

    const resource = {
      id: `${type}-${Date.now()}-${file.name}`,
      type,
      title: file.name,
      path: storedPath,
      size: file.size,
      mimeType: file.type,
      createdAt: new Date().toISOString()
    };

    const nextKnowledge = [...(values.knowledgeBase || []), resource];
    setFieldValue("knowledgeBase", nextKnowledge);
    toast.success("Arquivo adicionado à base de conhecimento!");
  }, [uploadKnowledgeFile]);

  const handleRemoveKnowledge = useCallback((resourceIndex, values, setFieldValue) => {
    const current = values.knowledgeBase || [];
    const next = current.filter((_, index) => index !== resourceIndex);
    setFieldValue("knowledgeBase", next);
  }, []);

  const isValidUrl = useCallback((url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleAddLinkResource = useCallback((values, setFieldValue) => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      toast.error("Informe o título e o link.");
      return;
    }

    if (!isValidUrl(linkForm.url.trim())) {
      toast.error("Informe um link válido (https://...).");
      return;
    }

    const resource = {
      id: `link-${Date.now()}`,
      type: "link",
      title: linkForm.title.trim(),
      url: linkForm.url.trim(),
      createdAt: new Date().toISOString()
    };

    const next = [...(values.knowledgeBase || []), resource];
    setFieldValue("knowledgeBase", next);
    setLinkForm({ title: "", url: "" });
    toast.success("Link adicionado à base de conhecimento!");
  }, [isValidUrl, linkForm]);

  const handleLinkInputChange = useCallback(
    field => event => {
      const value = event?.target?.value ?? "";
      setLinkForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleTextInputChange = useCallback(
    field => event => {
      const value = event?.target?.value ?? "";
      setTextForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  // O React executa a funcao de atualizacao DEPOIS, na re-renderizacao, e ate
  // la ele ja reciclou o objeto do evento e zerou o e.target. Ler o valor
  // dentro da funcao dava "Cannot read properties of null (reading 'value')"
  // e derrubava a tela. O valor tem que ser lido agora, fora dela.
  const handleFormulario = (field) => (e) => {
    const valor = e.target.value;
    setFormulario(prev => ({ ...prev, [field]: valor }));
  };

  // Regras de comportamento por perfil. Curtas de proposito: prompt grande
  // derruba a chamada de ferramenta.
  const REGRAS_DO_PERFIL = {
    atendimento: [
      'Descubra o que a pessoa precisa antes de explicar qualquer coisa.',
      'Antes de agendar, repita os dados e espere a confirmacao.',
      'Nunca marque, altere ou cancele nada sem a pessoa confirmar.'
    ],
    vendas: [
      'Descubra a necessidade antes de oferecer. Pergunte antes de recomendar.',
      'Recomende UMA opcao, nao a lista inteira.',
      'Trate objecao com informacao, sem insistir. Se a pessoa disser nao, aceite.',
      'Quando houver interesse, conduza ao pagamento em vez de so informar.'
    ],
    suporte: [
      'Resolva voce. Encaminhar e a ultima saida, nao a primeira.',
      'Se nao souber, diga que vai confirmar. Nunca invente solucao.',
      'Uma pergunta por vez ate entender o problema de verdade.'
    ]
  };

  // Gera o prompt (comportamento) e a base de conhecimento (fatos) a partir do
  // mesmo formulario. Separar os dois e o que mantem o prompt curto — e prompt
  // curto e o que faz a IA chamar as ferramentas.
  const gerarConfiguracaoDoFormulario = useCallback((values, setFieldValue) => {
    const f = formulario;
    const temAlgo = Object.keys(f).some(
      k => k !== 'perfil' && String(f[k] || '').trim()
    );
    if (!temAlgo) {
      toast.warn('Preencha ao menos um campo do formulario antes de gerar.');
      return;
    }

    const linha = (rotulo, valor) =>
      String(valor || '').trim() ? `${rotulo}: ${String(valor).trim()}` : '';

    // ── comportamento: vira prompt ──────────────────────────────────────────
    const quemEh = [
      f.nomeEmpresa?.trim()
        ? `Voce e o atendimento da ${f.nomeEmpresa.trim()}.`
        : 'Voce e o atendimento desta empresa.',
      f.funcaoIA?.trim() ? f.funcaoIA.trim() : ''
    ].filter(Boolean).join(' ');

    const comoFala = [
      f.tom?.trim() ? `Tom: ${f.tom.trim()}` : 'Fale como gente: frases curtas, direto ao ponto.',
      f.palavrasEvitar?.trim() ? `Nunca use: ${f.palavrasEvitar.trim()}` : '',
      'No maximo 4 linhas por mensagem. Blocos separados por linha em branco viram mensagens diferentes.',
      'Destaque com *asterisco*. Nunca use tabela.'
    ].filter(Boolean);

    const limites = [
      f.nuncaFazer?.trim() ? f.nuncaFazer.trim() : '',
      'Nao invente preco, prazo, recurso ou politica — consulte a base de conhecimento.',
      'Nao prometa desconto nem excecao. Nao peca senha nem cartao.'
    ].filter(Boolean);

    const transferir = [
      f.transferencia?.trim() ? f.transferencia.trim() : '',
      f.setoresTransferencia?.trim() ? `Setores: ${f.setoresTransferencia.trim()}` : '',
      'Se pedirem para falar com uma pessoa, transfira na hora.'
    ].filter(Boolean);

    // Habilidades sugeridas pelo perfil, somadas as que ja estavam marcadas.
    const jaMarcadas = values.toolsEnabled || [];
    const sugeridas = HABILIDADES_POR_PERFIL[f.perfil] || [];
    const habilidades = Array.from(new Set([...jaMarcadas, ...sugeridas]));

    // O prompt cita cada habilidade pelo nome e diz quando usar. Sem isto o
    // modelo escolhe so pela descricao e as vezes chama a errada.
    const comoUsar = {
      consultar_base_conhecimento: 'Duvida sobre a empresa, precos, servicos ou horarios: use consultar_base_conhecimento.',
      check_schedule_availability: 'Antes de oferecer horario: use check_schedule_availability.',
      create_schedule_appointment: 'Para marcar de fato: use create_schedule_appointment, so depois de confirmar os dados.',
      list_professionals: 'Para saber quem atende o que: use list_professionals.',
      get_company_schedule: 'Para dizer se esta aberto: use get_company_schedule.',
      list_schedule_appointments: 'Para ver o que ja esta marcado num dia: use list_schedule_appointments.',
      get_contact_info: 'Para saber com quem voce fala: use get_contact_info.',
      update_contact_info: 'Ao descobrir nome ou e-mail: use update_contact_info para guardar.',
      validate_email: 'Antes de aceitar um e-mail: use validate_email.',
      validate_phone: 'Antes de aceitar um telefone: use validate_phone.',
      validate_cpf: 'Antes de aceitar um CPF: use validate_cpf.',
      execute_command: 'Para transferir ou encerrar: use execute_command.',
      pause_bot: 'Quando uma pessoa assumir a conversa: use pause_bot.',
      notify_user: 'Para avisar alguem da equipe: use notify_user.',
      execute_tool: 'Para consultar o nosso sistema: use execute_tool.',
      list_available_tools: 'Se nao souber qual integracao existe: use list_available_tools.',
      jump_to_node: 'Em etapa que nao pode errar: use jump_to_node.'
    };

    const secaoFerramentas = habilidades
      .map(h => comoUsar[h])
      .filter(Boolean);

    const prompt = [
      quemEh,
      '',
      '## Ferramentas',
      'Use as ferramentas. Nunca diga que consultou, enviou ou tentou sem ter chamado a ferramenta.',
      secaoFerramentas.join('\n'),
      '',
      '## Como responder',
      comoFala.join('\n'),
      '',
      '## Como agir',
      (REGRAS_DO_PERFIL[f.perfil] || REGRAS_DO_PERFIL.atendimento).join('\n'),
      f.procedimento?.trim() ? f.procedimento.trim() : '',
      f.infoColeta?.trim() ? `Colete durante a conversa: ${f.infoColeta.trim()}` : '',
      '',
      '## Limites',
      limites.join('\n'),
      '',
      '## Quando transferir',
      transferir.join('\n')
    ].filter(l => l !== undefined).join('\n').replace(/\n{3,}/g, '\n\n').trim();

    setFieldValue('prompt', prompt);
    setFieldValue('toolsEnabled', habilidades);

    // ── fato: vira base de conhecimento ─────────────────────────────────────
    const itens = [];
    const juntar = (titulo, linhas) => {
      const corpo = linhas.filter(Boolean).join('\n');
      if (corpo) {
        itens.push({
          id: `form-${titulo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
          type: 'text',
          title: titulo,
          content: corpo,
          createdAt: new Date().toISOString()
        });
      }
    };

    juntar('Dados da empresa', [
      linha('Nome', f.nomeEmpresa),
      linha('Responsavel', f.responsavel),
      linha('Telefone', f.telefone),
      linha('E-mail', f.email),
      linha('Site', f.site),
      linha('Instagram', f.instagram),
      linha('Endereco', f.endereco)
    ]);

    juntar('Sobre o negocio', [
      linha('O que fazemos', f.oqueFaz),
      linha('Servicos e produtos', f.servicos),
      linha('Nosso diferencial', f.diferencial),
      linha('Regioes atendidas', f.regioes)
    ]);

    juntar('Horario de atendimento', [
      linha('Dias', f.diasAtendimento),
      linha('Horario', f.horario),
      linha('Feriados', f.feriados)
    ]);

    juntar('Precos e servicos', [f.precosServicos]);
    juntar('Perguntas frequentes', [f.perguntasFrequentes]);

    juntar('Agendamento', [
      linha('Trabalhamos com agendamento', f.agendamento),
      linha('Como agendar', f.comoAgendamento),
      linha('Dados necessarios', f.dadosAgendamento)
    ]);

    juntar('Como funciona o atendimento', [f.atendimentoHoje]);

    if (itens.length > 0) {
      const antigos = (values.knowledgeBase || []).filter(
        r => !String(r.id || '').startsWith('form-')
      );
      setFieldValue('knowledgeBase', [...antigos, ...itens]);
    }

    toast.success(
      `Pronto! Prompt com ${prompt.length} caracteres, ${itens.length} itens na base de conhecimento e ${habilidades.length} habilidades ligadas.`
    );
  }, [formulario]);

  const handleAddTextResource = useCallback((values, setFieldValue) => {
    if (!textForm.title.trim() || !textForm.content.trim()) {
      toast.error("Informe o título e o conteúdo do texto.");
      return;
    }

    const resource = {
      id: `text-${Date.now()}`,
      type: "text",
      title: textForm.title.trim(),
      content: textForm.content.trim(),
      createdAt: new Date().toISOString()
    };

    const next = [...(values.knowledgeBase || []), resource];
    setFieldValue("knowledgeBase", next);
    setTextForm({ title: "", content: "" });
    toast.success("Texto adicionado à base de conhecimento!");
  }, [textForm]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const availableTools = TOOL_CATALOG.map(tool => tool.value);

  // Agrupa por assunto mantendo a ordem em que os grupos aparecem no catalogo.
  const gruposDeHabilidades = useMemo(() => {
    const mapa = new Map();
    TOOL_CATALOG.forEach(t => {
      const g = t.grupo || "Outras";
      if (!mapa.has(g)) mapa.set(g, []);
      mapa.get(g).push(t);
    });
    return Array.from(mapa.entries());
  }, []);

  const openaiModels = [
    { value: "gpt-4o",                label: "GPT-4o — Visão + Áudio" },
    { value: "gpt-4o-mini",           label: "GPT-4o Mini — Visão (econômico)" },
    { value: "gpt-4o-audio-preview",  label: "GPT-4o Audio Preview — Áudio" },
    { value: "gpt-4-turbo",           label: "GPT-4 Turbo — Visão" }
  ];
  const geminiModels = [
    { value: "gemini-2.5-pro",       label: "Gemini 2.5 Pro — Visão + Áudio + Vídeo",  free: false },
    { value: "gemini-2.5-flash",     label: "Gemini 2.5 Flash — Visão + Áudio + Vídeo", free: false },
    { value: "gemini-2.0-flash",     label: "Gemini 2.0 Flash — Visão + Áudio + Vídeo", free: true  },
    { value: "gemini-1.5-pro",       label: "Gemini 1.5 Pro — Visão + Áudio + Vídeo",  free: false },
    { value: "gemini-1.5-flash",     label: "Gemini 1.5 Flash — Visão + Áudio",        free: false },
    { value: "gemini-1.5-flash-8b",  label: "Gemini 1.5 Flash-8B — Visão (leve)",     free: false }
  ];
  const grokModels = [
    { value: "grok-2-vision-latest", label: "Grok 2 Vision — Visão" },
    { value: "grok-2",               label: "Grok 2 — Texto" }
  ];

  const openrouterModels = [
    { value: "openai/gpt-4o",                     label: "GPT-4o — Visão + Áudio (OpenAI)" },
    { value: "openai/gpt-4o-mini",                label: "GPT-4o Mini — Econômico (OpenAI)" },
    { value: "anthropic/claude-3-5-sonnet",       label: "Claude 3.5 Sonnet — Melhor custo/qualidade" },
    { value: "anthropic/claude-3-haiku",          label: "Claude 3 Haiku — Rápido e econômico" },
    { value: "google/gemini-2.0-flash-001",       label: "Gemini 2.0 Flash — Google" },
    { value: "google/gemini-flash-1.5",           label: "Gemini 1.5 Flash — Google" },
    { value: "meta-llama/llama-3.3-70b-instruct", label: "LLaMA 3.3 70B — Meta (grátis)" },
    { value: "meta-llama/llama-3.1-8b-instruct",  label: "LLaMA 3.1 8B — Meta (grátis)" },
    { value: "mistralai/mistral-7b-instruct",     label: "Mistral 7B — Mistral (grátis)" },
    { value: "deepseek/deepseek-chat",            label: "DeepSeek Chat — Econômico" },
  ];

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Agente de IA Direto",
        btn: "Salvar",
      });
      const current = data?.data || {};
      try {
        // O corte antigo era 5.000 caracteres, e cortava no meio da palavra: o
        // formulario da empresa gera bem mais que isso, e tudo depois do corte
        // nunca chegava no modelo — a IA respondia generico sem ninguem saber
        // por que. Os modelos atuais aceitam ordens de grandeza a mais; o limite
        // aqui existe so para nao gravar algo absurdo no fluxo.
        const LIMITE_PROMPT = 100000;
        const promptValue = current.prompt || "Você é um assistente de IA útil e amigável.";
        const safePrompt = typeof promptValue === 'string' && promptValue.length <= LIMITE_PROMPT
          ? promptValue
          : String(promptValue).substring(0, LIMITE_PROMPT);
        
        // Recarrega o formulario da empresa se ele foi salvo com o no
        
        if (current.formularioEmpresa && typeof current.formularioEmpresa === "object") {
        
          setFormulario(prev => ({ ...prev, ...current.formularioEmpresa }));
        
        }
        
        setConfig({
          // O espalhamento vem primeiro de proposito: preserva o que estiver
          // gravado no no e nao tem campo na tela (pix, materiais, regua).
          // Sem ele, abrir o bloco e salvar apagava essas configuracoes.
          ...current,
          aiMode: current.aiMode || "system",
          provider: current.provider || "gemini",
          apiKey: current.apiKey || "",
          model: current.model || "",
          prompt: safePrompt,
          temperature: current.temperature || 0.7,
          maxTokens: current.maxTokens || 1000,
          entenderAudio: !!current.entenderAudio,
          entenderImagem: !!current.entenderImagem,
          entenderPdf: !!current.entenderPdf,
          identificarComprovante: !!current.identificarComprovante,
          isolarNumero: !!current.isolarNumero,
          maxMessages: current.maxMessages || 10,
          pauseMinutes: current.pauseMinutes || 10,
          voice: current.voice || "texto",
          voiceKey: current.voiceKey || "",
          voiceRegion: current.voiceRegion || "",
          ttsModel: current.ttsModel || "tts-1",
          audioPercentage: current.audioPercentage || 30,
          toolsEnabled: current.toolsEnabled || [],
          knowledgeBase: current.knowledgeBase || [],
          knowledgeBaseIds: current.knowledgeBaseIds || []
        });
        setSelectedVoice(current.voice || "texto");
        setAudioEnabled(current.voice && current.voice !== "texto");
        setTtsModel(current.ttsModel || "tts-1");
        setAudioPercentage(current.audioPercentage || 30);
      } catch (error) {
        console.error("Erro ao carregar configuração:", error);
        // Fallback para configuração padrão
        setConfig({
          provider: "gemini",
          apiKey: "",
          model: "",
          prompt: "Você é um assistente de IA útil e amigável.",
          temperature: 0.7,
          maxTokens: 1000,
          maxMessages: 10,
          voice: "texto",
          voiceKey: "",
          voiceRegion: "",
          ttsModel: "tts-1",
          audioPercentage: 30,
          toolsEnabled: [],
          knowledgeBase: [],
          knowledgeBaseIds: []
        });
      }
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Agente de IA Direto",
        btn: "Salvar",
      });
      setConfig({
        provider: "gemini",
        apiKey: "",
        model: "gemini-2.0-flash",
        prompt: "Você é um assistente de IA útil e amigável.",
        temperature: 0.7,
        maxTokens: 1000,
        maxMessages: 10,
        pauseMinutes: 10,
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        ttsModel: "tts-1",
        audioPercentage: 30,
        toolsEnabled: [],
        knowledgeBase: [],
        knowledgeBaseIds: []
      });
      setActiveModal(true);
    }

    return () => {
      // Cleanup
    };
  }, [open, data]);

  useEffect(() => {
    api.get("/knowledge-bases").then(({ data }) => setAvailableKnowledgeBases(data)).catch(() => {});
    api.get("/ai-settings").then(({ data }) => setAiIntegrationSettings(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/ai-credits/system-providers").then(({ data }) => setSystemProviders(data)).catch(() => {});
    // Etiquetas comuns e colunas do Kanban: as duas servem para parar a regua.
    Promise.all([
      api.get("/tags/list", { params: { kanban: 0 } }).then(({ data }) => data).catch(() => []),
      api.get("/tags/list", { params: { kanban: 1 } }).then(({ data }) => data).catch(() => [])
    ]).then(([comuns, colunas]) => {
      const todas = [...(Array.isArray(comuns) ? comuns : []), ...(Array.isArray(colunas) ? colunas : [])];
      setEtiquetasDaEmpresa(todas.filter(t => t && t.id).sort((a, b) => String(a.name).localeCompare(String(b.name))));
    });
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleToolToggle = (tool) => {
    setConfig(prev => ({
      ...prev,
      toolsEnabled: prev.toolsEnabled.includes(tool)
        ? prev.toolsEnabled.filter(t => t !== tool)
        : [...prev.toolsEnabled, tool]
    }));
  };

  const handleSave = (values) => {
    handleClose();
    onSave({
      ...values,
      voice: selectedVoice,
      ttsModel: ttsModel,
      audioPercentage: audioPercentage,
      // O formulario so gerava o prompt e sumia ao fechar o modal. Quem quisesse
      // ajustar uma linha tinha que redigitar tudo — e, quando o prompt salvo
      // foi truncado, nao havia de onde regerar. Agora ele fica guardado no no.
      formularioEmpresa: formulario
    });
  };

  const availableModels =
    config.provider === "gemini"     ? geminiModels     :
    config.provider === "grok"       ? grokModels       :
    config.provider === "openrouter" ? openrouterModels :
    openaiModels;

  return (
    <ErrorBoundary open={activeModal} close={handleClose}>
      <Dialog open={activeModal} onClose={handleClose} className={classes.root} maxWidth="md" fullWidth>
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <MdSmartToy style={{ marginRight: 8 }} />
            {labels.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Formik
            initialValues={config}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
              handleSave(values);
              actions.setSubmitting(false);
            }}
          >
            {({ values, setFieldValue, submitForm }) => (
              <Form style={{ width: "100%" }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="Configurações do Agente IA" variant="scrollable" scrollButtons="auto">
                  <Tab label="1. Prompt do Sistema" />
                  <Tab label="2. Configuração IA" />
                  <Tab label="3. Habilidades" />
                  <Tab label="4. Conhecimento" />
                  <Tab label="5. Voz" />
                  <Tab label="6. Régua de Silêncio" />
                </Tabs>

          {/* Aba 1: Prompt do Sistema */}
          <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <ChatIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Prompt do Sistema
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Defina como a IA deve se comportar, seu tom, personalidade e limitações. Seja específico para obter melhores resultados.
              </Typography>

              {/* ── Formulário de Implantação ── */}
              <Box style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setShowFormulario(v => !v)}
                  style={{ padding: '10px 16px', background: '#f3f4ff', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <AssignmentIcon style={{ color: '#6366f1', fontSize: 20 }} />
                    <Typography style={{ fontWeight: 700, fontSize: 14, color: '#3730a3' }}>
                      Formulário de Implantação
                    </Typography>
                    <Typography variant="caption" style={{ color: '#9e9e9e', marginLeft: 4 }}>
                      Preencha e gere um prompt completo automaticamente
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {showFormulario ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>

                <Collapse in={showFormulario}>
                  <Box style={{ padding: '16px', maxHeight: 520, overflowY: 'auto' }}>

                    {/* 1. Dados da Empresa */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      1. Dados da Empresa
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Nome da empresa" value={formulario.nomeEmpresa} onChange={handleFormulario('nomeEmpresa')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Nome do responsável" value={formulario.responsavel} onChange={handleFormulario('responsavel')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Telefone principal" value={formulario.telefone} onChange={handleFormulario('telefone')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="E-mail" value={formulario.email} onChange={handleFormulario('email')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Site" value={formulario.site} onChange={handleFormulario('site')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Instagram" value={formulario.instagram} onChange={handleFormulario('instagram')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Endereço" value={formulario.endereco} onChange={handleFormulario('endereco')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 2. Sobre o Negócio */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      2. Sobre o Negócio
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="O que sua empresa faz?" multiline rows={2} value={formulario.oqueFaz} onChange={handleFormulario('oqueFaz')} /></Grid>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Principais serviços/produtos oferecidos" multiline rows={2} value={formulario.servicos} onChange={handleFormulario('servicos')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Diferencial em relação à concorrência" value={formulario.diferencial} onChange={handleFormulario('diferencial')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Cidades/regiões atendidas" value={formulario.regioes} onChange={handleFormulario('regioes')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 3. Objetivo da IA */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      3. Objetivo da IA
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Função principal da IA (ex: Atendimento inicial, Qualificação de leads, Vendas, Suporte...)" value={formulario.funcaoIA} onChange={handleFormulario('funcaoIA')} /></Grid>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="O que você espera que a IA resolva?" multiline rows={2} value={formulario.expectativas} onChange={handleFormulario('expectativas')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 4. Horário de Atendimento */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      4. Horário de Atendimento
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Dias de atendimento" placeholder="Seg a Sex" value={formulario.diasAtendimento} onChange={handleFormulario('diasAtendimento')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Horário" placeholder="08h às 18h" value={formulario.horario} onChange={handleFormulario('horario')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Atende feriados?" placeholder="Sim / Não" value={formulario.feriados} onChange={handleFormulario('feriados')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 5. Serviços e Preços */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      5. Serviços e Preços
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Liste os serviços com nome, descrição e valor" multiline rows={4} placeholder={"Serviço: Consultoria\nDescrição: Análise do negócio\nValor: R$ 500\n\nServiço: Treinamento\n..."} value={formulario.precosServicos} onChange={handleFormulario('precosServicos')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 6. Perguntas Frequentes */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      6. Perguntas Frequentes
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Quais perguntas seus clientes mais fazem?" multiline rows={3} value={formulario.perguntasFrequentes} onChange={handleFormulario('perguntasFrequentes')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 7. Processo Comercial */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      7. Processo Comercial
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Como funciona o atendimento hoje?" multiline rows={2} value={formulario.atendimentoHoje} onChange={handleFormulario('atendimentoHoje')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Quais informações coletar do cliente?" value={formulario.infoColeta} onChange={handleFormulario('infoColeta')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Quando a IA deve transferir para humano?" value={formulario.transferencia} onChange={handleFormulario('transferencia')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 8. Agendamentos */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      8. Agendamentos
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Trabalha com agendamento?" placeholder="Sim / Não" value={formulario.agendamento} onChange={handleFormulario('agendamento')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Como são feitos hoje?" placeholder="Google Agenda, sistema próprio..." value={formulario.comoAgendamento} onChange={handleFormulario('comoAgendamento')} /></Grid>
                      <Grid item xs={4}><TextField fullWidth size="small" variant="outlined" label="Dados a coletar para agendar" value={formulario.dadosAgendamento} onChange={handleFormulario('dadosAgendamento')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 9. Tom de Comunicação */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      9. Tom de Comunicação
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Como a IA deve falar?" placeholder="Formal, Amigável, Técnica..." value={formulario.tom} onChange={handleFormulario('tom')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Palavras/expressões que a IA deve evitar" value={formulario.palavrasEvitar} onChange={handleFormulario('palavrasEvitar')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 10. Regras Importantes */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      10. Regras Importantes
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="O que a IA nunca deve fazer ou informar?" multiline rows={2} value={formulario.nuncaFazer} onChange={handleFormulario('nuncaFazer')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" variant="outlined" label="Procedimentos obrigatórios no atendimento" multiline rows={2} value={formulario.procedimento} onChange={handleFormulario('procedimento')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 11. Transferência para Equipe */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      11. Transferência para Equipe
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 12 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Setores que receberão transferências e quando" multiline rows={3} placeholder={"Comercial → quando o cliente quiser fechar\nFinanceiro → quando quiser pagar ou segunda via"} value={formulario.setoresTransferencia} onChange={handleFormulario('setoresTransferencia')} /></Grid>
                    </Grid>

                    <Divider style={{ marginBottom: 12 }} />

                    {/* 12. Base de Conhecimento */}
                    <Typography variant="caption" style={{ fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>
                      12. Base de Conhecimento
                    </Typography>
                    <Grid container spacing={2} style={{ marginBottom: 16 }}>
                      <Grid item xs={12}><TextField fullWidth size="small" variant="outlined" label="Quais bases acessar e em quais situações?" multiline rows={2} value={formulario.baseConhecimento} onChange={handleFormulario('baseConhecimento')} /></Grid>
                    </Grid>

                    {/* Perfil + gerar */}
                    <Box
                      p={2}
                      mt={2}
                      style={{
                        background: "#f0f9ff",
                        border: "1px solid #3b82f6",
                        borderRadius: 8
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{ color: "#1e40af", fontWeight: 600, marginBottom: 8 }}
                      >
                        O que esta IA vai fazer?
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={formulario.perfil}
                        onChange={handleFormulario('perfil')}
                        helperText="Muda as regras de comportamento geradas. O resto é igual."
                      >
                        <MenuItem value="atendimento">
                          Atender e agendar — clínica, salão, oficina
                        </MenuItem>
                        <MenuItem value="vendas">
                          Vender — produto físico ou infoproduto
                        </MenuItem>
                        <MenuItem value="suporte">
                          Dar suporte — software, assinatura, serviço
                        </MenuItem>
                      </TextField>
                    </Box>

                    {/* Botão Gerar */}
                    <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 12, paddingTop: 4 }}>
                      <Typography variant="caption" style={{ color: '#9e9e9e' }}>
                        Gera o prompt com o comportamento e joga preços, horários e perguntas na base de conhecimento
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => gerarConfiguracaoDoFormulario(values, setFieldValue)}
                        style={{ borderRadius: 20, textTransform: 'none', fontWeight: 700 }}
                      >
                        Gerar configuração
                      </Button>
                    </Box>

                  </Box>
                </Collapse>
              </Box>

              <Field
                as={TextField}
                label="Prompt do Sistema"
                name="prompt"
                multiline
                rows={18}
                fullWidth
                variant="outlined"
                margin="dense"
                className={classes.formControl}
              />

            </Box>
          </TabPanel>

          {/* Aba 2: Configuração IA */}
          <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="h6" style={{ marginBottom: 16 }}>
                Configuração de IA
              </Typography>


              <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
                Parâmetros do Modelo
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Ajuste a criatividade e o tamanho das respostas do modelo de IA.
              </Typography>

              <Box display="flex" gap={2}>
                <Field
                  as={TextField}
                  label="Temperature"
                  name="temperature"
                  type="number"
                  inputProps={{ min: 0, max: 2, step: 1 }}
                  className={classes.formControl}
                  helperText="0=preciso, 1=equilibrado, 2=criativo"
                />
                <Field
                  as={TextField}
                  label="Max Tokens"
                  name="maxTokens"
                  type="number"
                  inputProps={{ min: 1, max: 4096 }}
                  className={classes.formControl}
                  helperText="Tamanho máximo da resposta"
                />
              </Box>

              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 28, marginBottom: 8 }}>
                <ChatIcon style={{ marginRight: 8, color: '#10b981' }} />
                Reconhecimento
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Recursos extras para a IA entender o que o cliente envia.
              </Typography>

              <Box style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.entenderImagem}
                      onChange={(_, v) => setFieldValue("entenderImagem", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>Entender imagem</span>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.entenderPdf}
                      onChange={(_, v) => setFieldValue("entenderPdf", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>Entender PDF</span>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.entenderAudio}
                      onChange={(_, v) => setFieldValue("entenderAudio", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>Entender áudio do cliente</span>}
                />
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  A IA lê a mídia que o cliente enviou. O áudio é transcrito antes.
                  Essas opções consomem mais tokens.
                </Typography>
              </Box>

              <Box style={{ border: "1px solid #fdba74", background: "#fff7ed", borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.identificarComprovante}
                      onChange={(_, v) => setFieldValue("identificarComprovante", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14, fontWeight: 600, color: "#9a3412" }}>Identificar comprovante</span>}
                />
                <Typography variant="caption" style={{ display: "block", color: "#9a3412" }}>
                  A IA analisa a imagem ou PDF enviado e extrai os dados do pagamento
                  (valor, data, pagador, recebedor) e diz se parece autêntico.
                </Typography>
              </Box>

              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 28, marginBottom: 8 }}>
                <ChatIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Histórico de Mensagens
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Quantidade de mensagens anteriores que a IA deve considerar no contexto da conversa.
              </Typography>
              <Field
                as={TextField}
                label="Máximo de Mensagens"
                name="maxMessages"
                type="number"
                inputProps={{ min: 1, max: 50, step: 1 }}
                fullWidth
                className={classes.formControl}
                helperText="Recomendado: 10 mensagens"
              />

              <Box style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, marginTop: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!values.isolarNumero}
                      onChange={(_, v) => setFieldValue("isolarNumero", v)}
                    />
                  }
                  label={<span style={{ fontSize: 14, fontWeight: 600 }}>Isolar este número</span>}
                />
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  Desligado: se este mesmo fluxo atende mais de um número da empresa, a IA
                  lembra do que o cliente conversou nos outros números. Ligado: a IA só usa
                  o que foi conversado no número em que o cliente está.
                </Typography>
              </Box>

              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginTop: 28, marginBottom: 8 }}>
                <ScheduleIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Pausa da IA ao Responder Manualmente
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Quando você responder manualmente pelo sistema ou celular, a IA ficará pausada por este tempo antes de voltar a responder automaticamente.
              </Typography>
              <Field
                as={TextField}
                label="Tempo de Pausa (minutos)"
                name="pauseMinutes"
                type="number"
                inputProps={{ min: 1, max: 1440, step: 1 }}
                fullWidth
                className={classes.formControl}
                helperText="Padrão: 10 minutos. Máximo: 1440 (24h)"
              />
            </Box>
          </TabPanel>

          {/* Aba 3: Habilidades */}
          <TabPanel value={tabValue} index={2} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 6 }}>
                <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 16 }}>
                  <ExtensionIcon style={{ color: '#6366f1', fontSize: 20 }} />
                  Habilidades do Agente
                </Typography>
                <Typography variant="caption" style={{ background: '#f3f4f6', borderRadius: 20, padding: '4px 12px', color: '#6b7280', fontWeight: 600 }}>
                  {(values.toolsEnabled || []).length} ativa{(values.toolsEnabled || []).length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Ative somente as habilidades que este agente deve usar. As marcadas em vermelho são sensíveis — ative apenas se necessário.
              </Typography>
              <Box className={classes.toolGrid}>
                {gruposDeHabilidades.map(([grupo, ferramentas]) => (
                  <React.Fragment key={grupo}>
                    <Box
                      style={{
                        gridColumn: "1 / -1",
                        marginTop: 8,
                        marginBottom: 4,
                        borderBottom: "1px solid rgba(128,128,128,0.25)",
                        paddingBottom: 4
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{ fontWeight: 700, letterSpacing: 0.5, opacity: 0.75 }}
                      >
                        {grupo.toUpperCase()} · {ferramentas.filter(f => (values.toolsEnabled || []).includes(f.value)).length}/{ferramentas.length}
                      </Typography>
                    </Box>
                    {ferramentas.map(tool => {
                  const checked = (values.toolsEnabled || []).includes(tool.value);
                  const isSensitive = DEFAULT_SENSITIVE_TOOLS.includes(tool.value);
                  const Icon = TOOL_ICONS[tool.value] || MdSmartToy;
                  return (
                    <Paper
                      key={tool.value}
                      className={`${classes.toolCard} ${checked ? classes.toolCardActive : ''}`}
                      elevation={0}
                    >
                      <div className={classes.toolHeader}>
                        <div className={`${classes.toolIcon} ${isSensitive ? classes.toolIconSensitive : ''}`}>
                          <Icon style={{ fontSize: 18 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Typography className={classes.toolTitle}>
                            {tool.title}
                          </Typography>
                          <Typography className={classes.toolSlug}>
                            {tool.value}
                          </Typography>
                        </div>
                        {isSensitive && (
                          <Chip
                            size="small"
                            label="Sensível"
                            style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontSize: 10, height: 20, flexShrink: 0 }}
                          />
                        )}
                      </div>
                      <Typography className={classes.toolDescription}>
                        {tool.description}
                      </Typography>
                      <Typography className={classes.toolInstruction}>
                        {TOOL_INSTRUCTIONS[tool.value] || "Instrua a IA mencionando o nome da ferramenta quando precisar usá-la."}
                      </Typography>
                      <div className={classes.toolFooter}>
                        <Typography variant="caption" style={{ color: checked ? "#6366f1" : "#9ca3af", fontWeight: checked ? 600 : 400 }}>
                          {checked ? "● Ativa" : "○ Desativada"}
                        </Typography>
                        <Switch
                          color="primary"
                          checked={checked}
                          onChange={(_, newValue) => {
                            const current = values.toolsEnabled || [];
                            const next = newValue
                              ? [...current, tool.value]
                              : current.filter(name => name !== tool.value);
                            setFieldValue("toolsEnabled", next);
                          }}
                        />
                      </div>
                    </Paper>
                  );
                    })}
                  </React.Fragment>
                ))}
              </Box>

              {/* Chaves Pix — so aparece com a habilidade enviar_pix ligada.
                  A chave fica aqui, no bloco, e nao no banco: o cliente edita
                  sozinho. Aceita mais de uma oferta, cada uma com seu valor. */}
              {(values.toolsEnabled || []).includes("enviar_pix") && (() => {
                const lista = Array.isArray(values.pix)
                  ? values.pix
                  : (values.pix ? [values.pix] : []);

                const trocar = (indice, campo, valor) => {
                  const proxima = lista.map((item, i) =>
                    i === indice ? { ...item, [campo]: valor } : item
                  );
                  setFieldValue("pix", proxima);
                };

                return (
                  <Box style={{ marginTop: 24 }}>
                    <Divider style={{ marginBottom: 16 }} />
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 4 }}>
                      <Typography variant="h6" style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16 }}>
                        <ShoppingCartIcon style={{ color: "#059669", fontSize: 20 }} />
                        Chaves Pix do agente
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setFieldValue("pix", [...lista, {
                          itemName: "", tipoChave: "telefone", chave: "", nomeRecebedor: "",
                          cidade: "", valor: "", formato: "cartao",
                          rotuloBotao: "Copiar chave Pix", bodyText: ""
                        }])}
                      >
                        Adicionar
                      </Button>
                    </Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      O agente nunca digita a chave na conversa: ele chama a habilidade e a plataforma monta o Pix.
                      Cadastre uma oferta por valor — a IA escolhe pelo nome da oferta.
                    </Typography>

                    {lista.length === 0 && (
                      <Paper style={{ padding: 20, textAlign: "center", border: "1px dashed #d1d5db", backgroundColor: "#f9fafb" }}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhuma chave cadastrada. A habilidade esta ligada, mas o agente nao tem o que enviar.
                        </Typography>
                      </Paper>
                    )}

                    {lista.map((item, indice) => (
                      <Paper key={indice} style={{ padding: 16, marginBottom: 12, border: "1px solid #e5e7eb" }} elevation={0}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 12 }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {item.itemName || `Oferta ${indice + 1}`}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setFieldValue("pix", lista.filter((_, i) => i !== indice))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Nome da oferta"
                              placeholder="Ex: Recheios Sem Fogo"
                              helperText="E por este nome que a IA escolhe a chave"
                              value={item.itemName || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "itemName", v); }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <InputLabel>Tipo da chave</InputLabel>
                              <Select
                                label="Tipo da chave"
                                value={item.tipoChave || "telefone"}
                                onChange={(e) => { const v = e.target.value; trocar(indice, "tipoChave", v); }}
                              >
                                <MenuItem value="cpf">CPF</MenuItem>
                                <MenuItem value="cnpj">CNPJ</MenuItem>
                                <MenuItem value="email">E-mail</MenuItem>
                                <MenuItem value="telefone">Telefone</MenuItem>
                                <MenuItem value="aleatoria">Aleatoria</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Valor"
                              placeholder="10,99"
                              helperText="Vazio = o cliente escolhe"
                              value={item.valor || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "valor", v); }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Chave Pix"
                              value={item.chave || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "chave", v); }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Nome do recebedor"
                              value={item.nomeRecebedor || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "nomeRecebedor", v); }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Cidade"
                              value={item.cidade || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "cidade", v); }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <InputLabel>Formato do envio</InputLabel>
                              <Select
                                label="Formato do envio"
                                value={item.formato || "cartao"}
                                onChange={(e) => { const v = e.target.value; trocar(indice, "formato", v); }}
                              >
                                <MenuItem value="cartao">Cartao com botao</MenuItem>
                                <MenuItem value="chave">So a chave</MenuItem>
                                <MenuItem value="emv">Codigo copia e cola</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Rotulo do botao"
                              value={item.rotuloBotao || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "rotuloBotao", v); }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Mensagem que acompanha"
                              placeholder="Ex: E so copiar a chave e pagar pelo aplicativo do banco"
                              value={item.bodyText || ""}
                              onChange={(e) => { const v = e.target.value; trocar(indice, "bodyText", v); }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                );
              })()}

              {/* Entregaveis — so aparece com a habilidade enviar_material
                  ligada. Reaproveita o mesmo upload da base de conhecimento. */}
              {(values.toolsEnabled || []).includes("enviar_material") && (() => {
                const materiais = Array.isArray(values.materiais) ? values.materiais : [];

                const trocarMat = (indice, campo, valor) => {
                  setFieldValue("materiais", materiais.map((m, i) =>
                    i === indice ? { ...m, [campo]: valor } : m
                  ));
                };

                const subirArquivos = async (indice, listaDeArquivos) => {
                  const caminhos = [];
                  for (const arquivo of Array.from(listaDeArquivos || [])) {
                    const caminho = await uploadKnowledgeFile(arquivo);
                    if (caminho) caminhos.push(caminho);
                  }
                  if (!caminhos.length) return;
                  const atual = materiais[indice] || {};
                  trocarMat(indice, "arquivos", [...(atual.arquivos || []), ...caminhos]);
                  toast.success(caminhos.length > 1 ? "Arquivos adicionados!" : "Arquivo adicionado!");
                };

                return (
                  <Box style={{ marginTop: 24 }}>
                    <Divider style={{ marginBottom: 16 }} />
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 4 }}>
                      <Typography variant="h6" style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16 }}>
                        <AttachFileIcon style={{ color: "#0ea5e9", fontSize: 20 }} />
                        Materiais que o agente entrega
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setFieldValue("materiais", [...materiais, {
                          nome: "", descricao: "", mensagemAntes: "", arquivos: []
                        }])}
                      >
                        Adicionar
                      </Button>
                    </Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      O agente decide a hora de entregar, nunca o que entregar. Havendo mais de um material,
                      a IA escolhe pelo nome.
                    </Typography>

                    {materiais.length === 0 && (
                      <Paper style={{ padding: 20, textAlign: "center", border: "1px dashed #d1d5db", backgroundColor: "#f9fafb" }}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhum material cadastrado. A habilidade esta ligada, mas o agente nao tem o que entregar.
                        </Typography>
                      </Paper>
                    )}

                    {materiais.map((material, indice) => (
                      <Paper key={indice} style={{ padding: 16, marginBottom: 12, border: "1px solid #e5e7eb" }} elevation={0}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 12 }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {material.nome || `Material ${indice + 1}`}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setFieldValue("materiais", materiais.filter((_, i) => i !== indice))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Nome do material"
                              placeholder="Ex: Receitas"
                              helperText="E por este nome que a IA escolhe"
                              value={material.nome || ""}
                              onChange={(e) => { const v = e.target.value; trocarMat(indice, "nome", v); }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Descricao"
                              placeholder="Ex: As 5 apostilas em PDF"
                              value={material.descricao || ""}
                              onChange={(e) => { const v = e.target.value; trocarMat(indice, "descricao", v); }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth variant="outlined" size="small" label="Mensagem antes do envio"
                              placeholder="Ex: Prontinho! Estou te enviando agora"
                              value={material.mensagemAntes || ""}
                              onChange={(e) => { const v = e.target.value; trocarMat(indice, "mensagemAntes", v); }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                              <Typography variant="caption" style={{ fontWeight: 600 }}>
                                Arquivos ({(material.arquivos || []).length})
                              </Typography>
                              <Button size="small" variant="outlined" component="label" disabled={knowledgeUploading}>
                                {knowledgeUploading ? "Enviando..." : "Anexar arquivos"}
                                <input
                                  type="file"
                                  hidden
                                  multiple
                                  onChange={(e) => {
                                    const arquivos = e.target.files;
                                    subirArquivos(indice, arquivos);
                                    e.target.value = "";
                                  }}
                                />
                              </Button>
                            </Box>
                            {(material.arquivos || []).map((caminho, iArquivo) => (
                              <Box
                                key={iArquivo}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ background: "#f9fafb", borderRadius: 6, padding: "6px 10px", marginBottom: 6 }}
                              >
                                <Typography variant="caption" style={{ wordBreak: "break-all" }}>
                                  {String(caminho).split("/").pop()}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => trocarMat(indice, "arquivos", (material.arquivos || []).filter((_, i) => i !== iArquivo))}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </TabPanel>

          {/* Aba 4: Base de Conhecimento */}
          <TabPanel value={tabValue} index={3} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="subtitle2" gutterBottom>
                <LibraryBooksIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Bases de Conhecimento
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Selecione as bases de conhecimento que este agente pode consultar. Nas bases vinculadas, instrua a IA no prompt sobre quando e como usá-las.
              </Typography>

              {availableKnowledgeBases.length === 0 ? (
                <Paper style={{ padding: 24, textAlign: 'center', border: '1px dashed #d1d5db', backgroundColor: '#f9fafb' }}>
                  <LibraryBooksIcon style={{ fontSize: 40, color: '#ccc', marginBottom: 8 }} />
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma base de conhecimento criada ainda.
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Acesse <strong>Automação → Bases de Conhecimento</strong> para criar suas bases.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={1}>
                  {availableKnowledgeBases.map(base => {
                    const isSelected = (config.knowledgeBaseIds || []).includes(base.id);
                    return (
                      <Grid item xs={12} sm={6} key={base.id}>
                        <Paper
                          style={{
                            padding: 12,
                            border: `2px solid ${isSelected ? '#6366f1' : '#e5e7eb'}`,
                            backgroundColor: isSelected ? '#eef2ff' : '#fff',
                            cursor: 'pointer',
                            borderRadius: 8
                          }}
                          onClick={() => {
                            const ids = config.knowledgeBaseIds || [];
                            setConfig(prev => ({
                              ...prev,
                              knowledgeBaseIds: isSelected
                                ? ids.filter(id => id !== base.id)
                                : [...ids, base.id]
                            }));
                          }}
                        >
                          <Box display="flex" alignItems="flex-start" style={{ gap: 10 }}>
                            <LibraryBooksIcon color={isSelected ? "primary" : "action"} fontSize="small" style={{ marginTop: 2 }} />
                            <Box flex={1}>
                              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{base.name}</Typography>
                              {base.description && (
                                <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                                  {base.description}
                                </Typography>
                              )}
                              <Chip
                                label={`${base.items?.length || 0} itens`}
                                size="small"
                                style={{ marginTop: 4, fontSize: 10, height: 20 }}
                                color={isSelected ? "primary" : "default"}
                              />
                            </Box>
                            {isSelected && (
                              <Chip label="Vinculada" color="primary" size="small" style={{ fontSize: 10, height: 20 }} />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {(config.knowledgeBaseIds || []).length > 0 && (
                <Box style={{ marginTop: 16, padding: 12, backgroundColor: '#f0f4ff', borderRadius: 8, border: '1px solid #c5d0f5' }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Dica:</strong> No prompt deste agente, instrua a IA sobre quando consultar cada base. Exemplo: <em>"Quando o contato perguntar sobre tratamentos, consulte a base de conhecimento 'Tratamentos' para obter as informações corretas."</em>
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Aba 5: Voz */}
          <TabPanel value={tabValue} index={4} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <RecordVoiceOverIcon style={{ marginRight: 8, color: '#6366f1' }} />
                Configuração de Resposta por Áudio
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Configure se a IA responde só em texto ou também com mensagem de voz.
              </Typography>

              <VozNatural
                valor={values.vozFloow}
                onChange={v => setFieldValue("vozFloow", v)}
                subir={uploadKnowledgeFile}
              />

              {/* Voz antiga (vozes prontas em ingles da OpenAI): substituida pela
                  voz natural acima. So aparece em bloco que ainda usa, para poder desligar. */}
              {audioEnabled && (
              <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: "#f8f9fa" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={audioEnabled}
                      onChange={(e) => {
                        setAudioEnabled(e.target.checked);
                        setSelectedVoice(e.target.checked ? "alloy" : "texto");
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      <VolumeUpIcon style={{ marginRight: 8 }} />
                      <Typography variant="body1">
                        Habilitar resposta por áudio
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
              )}

              {audioEnabled && (
                <>
                  <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: "#e3f2fd", border: "1px solid #90caf9" }}>
                    <Box display="flex" alignItems="center">
                      <InfoIcon style={{ marginRight: 8, color: "#1976d2" }} />
                      <Typography variant="body2" style={{ color: "#1565c0" }}>
                        Usa a mesma API Key configurada na aba "Configuração IA"
                      </Typography>
                    </Box>
                  </Paper>

                  <FormControl variant="outlined" className={classes.formControl} margin="dense" fullWidth>
                    <InputLabel>Modelo TTS</InputLabel>
                    <Select
                      value={ttsModel}
                      onChange={(e) => setTtsModel(e.target.value)}
                      label="Modelo TTS"
                    >
                      <MenuItem value="tts-1">
                        <Box>
                          <Typography variant="body1">tts-1</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Rápido e eficiente
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="tts-1-hd">
                        <Box>
                          <Typography variant="body1">tts-1-hd</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Alta qualidade (mais lento)
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: "#fff3e0", border: "1px solid #ffb74d" }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600, color: "#e65100" }}>
                      🎲 Probabilidade de Resposta em Áudio
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      Controle a porcentagem de respostas que serão enviadas em áudio. O restante será enviado como texto.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" style={{ minWidth: 40, fontWeight: 600, color: "#e65100" }}>
                        {audioPercentage}%
                      </Typography>
                      <Slider
                        value={audioPercentage}
                        onChange={(e, newValue) => setAudioPercentage(newValue)}
                        min={0}
                        max={100}
                        step={5}
                        marks={[
                          { value: 0, label: '0%' },
                          { value: 25, label: '25%' },
                          { value: 50, label: '50%' },
                          { value: 75, label: '75%' },
                          { value: 100, label: '100%' }
                        ]}
                        valueLabelDisplay="auto"
                        style={{ flex: 1 }}
                      />
                    </Box>
                  </Paper>

                  <FormControl variant="outlined" className={classes.formControl} margin="dense" fullWidth>
                    <InputLabel>Voz</InputLabel>
                    <Select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      label="Voz"
                    >
                      <MenuItem value="alloy">
                        <Box>
                          <Typography variant="body1">Alloy</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Neutro e equilibrado
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="echo">
                        <Box>
                          <Typography variant="body1">Echo</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Masculino e claro
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="fable">
                        <Box>
                          <Typography variant="body1">Fable</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Britânico e expressivo
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="onyx">
                        <Box>
                          <Typography variant="body1">Onyx</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Grave e profundo
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="nova">
                        <Box>
                          <Typography variant="body1">Nova</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Feminino e energético
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="shimmer">
                        <Box>
                          <Typography variant="body1">Shimmer</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Feminino e suave
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Paper style={{ padding: 16, marginTop: 16, backgroundColor: "#f5f5f5" }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      🎧 Preview das Vozes:
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { name: "alloy", label: "Alloy", desc: "Neutro e equilibrado", url: "https://cdn.openai.com/API/docs/audio/alloy.wav" },
                        { name: "echo", label: "Echo", desc: "Masculino e claro", url: "https://cdn.openai.com/API/docs/audio/echo.wav" },
                        { name: "fable", label: "Fable", desc: "Britânico e expressivo", url: "https://cdn.openai.com/API/docs/audio/fable.wav" },
                        { name: "onyx", label: "Onyx", desc: "Grave e profundo", url: "https://cdn.openai.com/API/docs/audio/onyx.wav" },
                        { name: "nova", label: "Nova", desc: "Feminino e energético", url: "https://cdn.openai.com/API/docs/audio/nova.wav" },
                        { name: "shimmer", label: "Shimmer", desc: "Feminino e suave", url: "https://cdn.openai.com/API/docs/audio/shimmer.wav" }
                      ].map((voice) => (
                        <Grid item xs={12} sm={6} key={voice.name}>
                          <Paper 
                            style={{ 
                              padding: 12, 
                              backgroundColor: selectedVoice === voice.name ? "#e3f2fd" : "#fff",
                              border: selectedVoice === voice.name ? "2px solid #1976d2" : "1px solid #ddd",
                              cursor: "pointer",
                              transition: "all 0.2s"
                            }}
                            onClick={() => setSelectedVoice(voice.name)}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box flex={1}>
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                  {voice.label}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {voice.desc}
                                </Typography>
                              </Box>
                              <Tooltip title="Ouvir preview">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const audio = new Audio(voice.url);
                                    audio.play().catch(err => console.log("Erro ao reproduzir áudio:", err));
                                  }}
                                >
                                  <PlayArrowIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </>
              )}
            </Box>
          </TabPanel>

          {/* Aba 6: Regua de silencio — o que fazer quando o cliente some.
              Fica aqui, no bloco, para o cliente ajustar os tempos sozinho. */}
          <TabPanel value={tabValue} index={5} className={classes.tabPanel}>
            <Box className={classes.tabContent}>
              {(() => {
                const regua = (values.regua && typeof values.regua === "object") ? values.regua : {};
                const degraus = Array.isArray(regua.degraus) ? regua.degraus : [];
                const gravar = (proxima) => setFieldValue("regua", { ...regua, ...proxima });
                const trocarDegrau = (indice, campo, valor) => {
                  gravar({ degraus: degraus.map((d, i) => i === indice ? { ...d, [campo]: valor } : d) });
                };
                const atalhos = [
                  { rotulo: "30 min", minutos: 30 },
                  { rotulo: "2 horas", minutos: 120 },
                  { rotulo: "6 horas", minutos: 360 },
                  { rotulo: "24 horas", minutos: 1440 },
                  { rotulo: "3 dias", minutos: 4320 }
                ];

                return (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 4 }}>
                      <Typography variant="h6" style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16 }}>
                        <ScheduleIcon style={{ color: "#f59e0b", fontSize: 20 }} />
                        Quando o cliente para de responder
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={regua.ativa !== false && degraus.length > 0}
                            onChange={(_, ligado) => gravar({ ativa: ligado })}
                          />
                        }
                        label={regua.ativa !== false && degraus.length > 0 ? "Ligada" : "Desligada"}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      Cada degrau e uma tentativa de retomar a conversa, contada a partir do silencio do cliente.
                      Se ele responder, a sequencia para sozinha e recomeca do zero na proxima vez.
                    </Typography>

                    <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 6 }}>
                      <InputLabel>Parar a régua quando tiver estas etiquetas</InputLabel>
                      <Select
                        multiple
                        label="Parar a régua quando tiver estas etiquetas"
                        value={(Array.isArray(regua.pararComEtiquetas) ? regua.pararComEtiquetas : []).map(Number)}
                        onChange={(e) => { const v = e.target.value; gravar({ pararComEtiquetas: (Array.isArray(v) ? v : []).map(Number) }); }}
                        renderValue={(selecionadas) => (
                          <Box display="flex" style={{ gap: 4, flexWrap: "wrap" }}>
                            {selecionadas.map(id => {
                              const t = etiquetasDaEmpresa.find(x => Number(x.id) === Number(id));
                              return <Chip key={id} size="small" label={t ? t.name : `Etiqueta ${id}`} style={t && t.color ? { backgroundColor: t.color, color: "#fff" } : undefined} />;
                            })}
                          </Box>
                        )}
                      >
                        {etiquetasDaEmpresa.map(t => (
                          <MenuItem key={t.id} value={Number(t.id)}>{t.name}{Number(t.kanban) === 1 ? " (Kanban)" : ""}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 16 }}>
                      Ex.: a etiqueta de quem já comprou ou foi para o suporte. Com uma delas na conversa ou no contato, nenhuma tentativa sai, nem as que já estavam agendadas. A IA continua respondendo quando a pessoa escrever.
                    </Typography>

                    <Box display="flex" justifyContent="flex-end" style={{ marginBottom: 12 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => gravar({
                          ativa: true,
                          degraus: [...degraus, { minutos: 120, modo: "ia", instrucao: "", texto: "" }]
                        })}
                      >
                        Adicionar degrau
                      </Button>
                    </Box>

                    {degraus.length === 0 && (
                      <Paper style={{ padding: 20, textAlign: "center", border: "1px dashed #d1d5db", backgroundColor: "#f9fafb" }}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhum degrau. O agente nao vai cutucar quem sumir.
                        </Typography>
                      </Paper>
                    )}

                    {degraus.map((degrau, indice) => (
                      <Paper key={indice} style={{ padding: 16, marginBottom: 12, border: "1px solid #e5e7eb" }} elevation={0}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 12 }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {indice + 1}ª tentativa
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => gravar({ degraus: degraus.filter((_, i) => i !== indice) })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth variant="outlined" size="small" type="number" label="Esperar (minutos)"
                              value={degrau.minutos === undefined ? "" : degrau.minutos}
                              onChange={(e) => { const v = e.target.value; trocarDegrau(indice, "minutos", Number(v) || 0); }}
                              helperText={(() => {
                                const m = Number(degrau.minutos) || 0;
                                if (!m) return "";
                                if (m < 60) return `${m} minutos`;
                                if (m < 1440) return `${(m / 60).toFixed(1).replace(".0", "")} horas`;
                                return `${(m / 1440).toFixed(1).replace(".0", "")} dias`;
                              })()}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Box display="flex" alignItems="center" style={{ gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                              {atalhos.map(a => (
                                <Chip
                                  key={a.minutos}
                                  size="small"
                                  label={a.rotulo}
                                  variant={Number(degrau.minutos) === a.minutos ? "default" : "outlined"}
                                  onClick={() => trocarDegrau(indice, "minutos", a.minutos)}
                                />
                              ))}
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControl fullWidth variant="outlined" size="small">
                              <InputLabel>O que enviar</InputLabel>
                              <Select
                                label="O que enviar"
                                value={degrau.modo || "ia"}
                                onChange={(e) => { const v = e.target.value; trocarDegrau(indice, "modo", v); }}
                              >
                                <MenuItem value="ia">A IA escreve na hora</MenuItem>
                                <MenuItem value="texto">Texto fixo</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            {(degrau.modo || "ia") === "ia" ? (
                              <TextField
                                fullWidth multiline minRows={2} variant="outlined" size="small"
                                label="Instrucao para a IA"
                                placeholder="Ex: Volte leve, sem cobrar, so pra saber se ficou alguma duvida."
                                value={degrau.instrucao || ""}
                                onChange={(e) => { const v = e.target.value; trocarDegrau(indice, "instrucao", v); }}
                              />
                            ) : (
                              <TextField
                                fullWidth multiline minRows={2} variant="outlined" size="small"
                                label="Texto exato que sai"
                                placeholder="Ex: Oi! Passando pra saber se você ainda tem interesse."
                                value={degrau.texto || ""}
                                onChange={(e) => { const v = e.target.value; trocarDegrau(indice, "texto", v); }}
                              />
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  color="primary"
                                  checked={!!degrau.soSemResposta}
                                  onChange={(e) => { const v = e.target.checked; trocarDegrau(indice, "soSemResposta", v); }}
                                />
                              }
                              label="Só se a pessoa ainda não respondeu nada (retomada da abertura)"
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </>
                );
              })()}
            </Box>
          </TabPanel>
              </Form>
            )}
          </Formik>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            style={{
              color: "white",
              backgroundColor: "#db6565",
            }}
            variant="contained"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const form = document.querySelector('form');
              if (form) {
                form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }
            }}
            startIcon={<SaveIcon />}
            style={{
              color: "white",
              backgroundColor: "#437db5",
            }}
            variant="contained"
            className={classes.btnWrapper}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorBoundary>
  );
};

export default FlowBuilderAddDirectOpenAIModal;
